import type { TransferAgentBinding, TransferDestinationKind, TransferAvailabilityTarget } from 'voxr-shared';
import { Component, computed, effect, inject, input, model, TemplateRef, ViewChild } from '@angular/core';
import { UserService } from '../../../services/user.service';
import { AgentInfo, AvailabilityWindow, isValidUsPhoneNumber, SoftphoneStateDto, WEEKDAYS } from 'voxr-shared';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { InputSwitchModule } from 'primeng/inputswitch';
import { RippleModule } from 'primeng/ripple';
import { interval } from 'rxjs';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { TimezoneComponent } from '../timezone/timezone.component';
import { StateSelectorComponent } from '../state-selector/state-selector.component';
import { MatTableModule } from '@angular/material/table';
import { MatExpansionModule } from '@angular/material/expansion';
import { AppService } from '../../../services/app.service';
import { MatMenuModule } from '@angular/material/menu';
import { MatDividerModule } from '@angular/material/divider';
import { MatSelectModule } from '@angular/material/select';

@Component({
  selector: 'app-transfer-availability',
  standalone: true,
  imports: [
    CommonModule, FormsModule,
    ButtonModule, InputTextModule, InputSwitchModule, RippleModule,
    MatTooltipModule, MatDialogModule, MatCheckboxModule, MatInputModule, MatFormFieldModule, MatButtonModule,
    MatIconModule, MatProgressBarModule, MatButtonToggleModule, MatTableModule,
    MatExpansionModule, MatMenuModule, MatDividerModule, MatSelectModule,
    TimezoneComponent, StateSelectorComponent
  ],
  templateUrl: './transfer-availability.component.html',
})

export class TransferAvailabilityComponent {
  dialog = inject(MatDialog);
  private userService = inject(UserService);
  private app = inject(AppService);
  @ViewChild('settingsDialog') settingsDialog!: TemplateRef<any>;

  mode = input<'info' | 'edit'>('info');
  displayMode = input<'compact' | 'full'>('compact');
  disabled = input<boolean>(false);
  isVirtualAgent = input<boolean>(false);
  softphoneState = input<SoftphoneStateDto | undefined>(undefined);
  softphoneRuntimeState = input<'inactive' | 'loading' | 'ready' | 'error'>('inactive');

  transferBinding = model<TransferAgentBinding | undefined>();
  destinationKind = input<TransferDestinationKind | 'provider' | undefined>();
  protected providerTarget = computed<TransferAvailabilityTarget | undefined>(() => {
    const binding = this.transferBinding(); const kind = this.destinationKind();
    if (!binding || !kind) return undefined;
    if (kind === 'provider') return { integrationId: binding.integrationId, dynamic: true, deferredDestination: true };
    const address = binding.destination?.trim();
    const valid = kind === 'phone' ? isValidUsPhoneNumber(address) : !!address && /^(?:sip:)?[a-zA-Z0-9_.!~*'()%+-]+@[a-zA-Z0-9.-]+(?::[0-9]+)?(?:;transport=(?:udp|tcp|tls))?$/i.test(address);
    return valid ? { integrationId: binding.integrationId, dynamic: false, deferredDestination: false, destination: { kind, address: address! } } : undefined;
  });

  agentInfo = model.required<AgentInfo | undefined>();

  available: boolean = false;
  isSoftphoneConnecting: boolean = false;
  statusText: string = ' ';

  constructor() {
    interval(60000).pipe(takeUntilDestroyed()).subscribe(() => this.checkAvailable());
    effect(() => {
      const agent = this.agentInfo();
      this.softphoneState(); this.transferBinding(); this.destinationKind();
      if (!agent) return;

      const currentSchedule = agent.liveTransfer?.schedule || [];
      const hasAllWeekdays = WEEKDAYS.every((d: string) => currentSchedule.some(s => s.day === d));
      const hasSoftPhoneConfig = !!agent.liveTransfer?.softPhone && !!agent.liveTransfer?.targetPriority;
      if (!agent.liveTransfer || !hasAllWeekdays || !hasSoftPhoneConfig) {
        this.agentInfo.update(prev => prev ? {
          ...prev,
          liveTransfer: {
            ...(prev.liveTransfer || {}),
            targetPriority: prev.liveTransfer?.targetPriority || 'phone-number',
            onTransferAction: prev.liveTransfer?.onTransferAction || 'resume',
            softPhone: {
              enabled: prev.liveTransfer?.softPhone?.enabled || false,
              status: prev.liveTransfer?.softPhone?.status || 'inactive',
              ...(prev.liveTransfer?.softPhone || {}),
            },
            schedule: WEEKDAYS
              .map((d: string) => currentSchedule.find(s => s.day === d) || { day: d, from: '09:00', to: '17:00', enabled: false } as AvailabilityWindow),
          },
        } : prev);
      }
      this.checkAvailable();
    }, { allowSignalWrites: true });
  }

  protected hasValidPhone = computed(() => {
    const agent = this.agentInfo();
    if (this.transferBinding()) return !!this.providerTarget();
    if (!agent || !agent.liveTransfer?.phone) return true;
    return isValidUsPhoneNumber(agent.liveTransfer.phone);
  });

  private checkAvailable() {
    if (!this.agentInfo()) return;

    const softphoneState = this.softphoneState();
    const result = this.transferBinding() && !this.providerTarget()
      ? { available: false, statusText: 'Destination required' }
      : this.userService.isAvailable(this.agentInfo()!, softphoneState, this.providerTarget());
    const { available, statusText } = result;
    this.available = available;
    this.isSoftphoneConnecting = !available
      && !!softphoneState?.enabled
      && softphoneState.availabilityState === 'unavailable'
      && softphoneState.unavailableReason === 'no-active-connections'
      && this.softphoneRuntimeState() === 'loading';
    this.statusText = this.isSoftphoneConnecting ? 'Connecting soft phone...' : this.transferBinding() ? `Local eligibility: ${statusText}` : statusText;
  }

  ngOnInit() {
    this.checkAvailable();
  }

  get transferTargetLabel() {
    const liveTransfer = this.agentInfo()?.liveTransfer;
    if (this.transferBinding()) return this.destinationKind() === 'provider' ? 'Destination supplied during reservation' : this.transferBinding()?.destination || 'Destination required';
    if (this.isVirtualAgent()) return liveTransfer?.phone || 'Agent phone number';

    switch (liveTransfer?.targetPriority) {
      case 'soft-phone': return 'Soft phone';
      case 'fallback':
        return 'Soft phone or phone number';
      case 'phone-number':
      default: return liveTransfer?.phone || 'Agent phone number';
    }
  }

  get softPhoneStatusText() {
    if (this.isVirtualAgent()) return '';
    const softphoneState = this.softphoneState();
    if (!softphoneState?.enabled) return 'Disabled';

    switch (softphoneState.availabilityState) {
      case 'available': return 'Active';
      case 'busy': return 'Busy';
      default:
        switch (softphoneState.unavailableReason) {
          case 'connection-error': return 'Error';
          case 'softphone-disabled': return 'Disabled';
          default: return 'Inactive';
        }
    }
  }

  setAvailability() {
    this.checkAvailable();

    if (this.disabled()) return;

    const agent = this.agentInfo();
    if (!agent) return;

    if (agent.liveTransfer?.softPhone && !agent.liveTransfer.softPhone.enabled) {
      agent.liveTransfer.targetPriority = 'phone-number';
    }

    if (agent.liveTransfer?.phone && !isValidUsPhoneNumber(agent.liveTransfer.phone)) {
      this.app.message$.next({ severity: 'danger', summary: 'Availability info not updated!', detail: 'Invalid phone number format. Please enter a valid US phone number.' });
      return;
    }

    this.agentInfo.update(prev => prev ? { ...prev } : prev);
  }

  setAutoAnswerMode(autoAnswerMode: 'all' | 'campaign') {
    if (this.disabled()) return;
    this.agentInfo.update(agent => agent ? {
      ...agent,
      liveTransfer: {
        ...agent.liveTransfer,
        softPhone: { ...agent.liveTransfer?.softPhone, autoAnswerMode },
      },
    } : agent);
    this.setAvailability();
  }

  getOnTransferAction() {
    const agent = this.agentInfo();
    return (['pause', 'paused'].includes(agent?.liveTransfer?.onTransferAction || '')) ? 'pause' : 'resume';
  }
}
